import {
  BaseBodyTextStyles,
  BodyText,
  UIText,
  breakpoint,
  CheckCircleIcon,
  EyeIcon,
  HeartIcon,
  TimesCircleIcon,
  DropDown,
} from '@grays-npm/component-library';
import { PromoteItem } from '../../components/PromoteItem';
import React from 'react';
import TagManager from 'react-gtm-module';
import { useModal } from '../../state/modal';
import styled, { css } from 'styled-components';
import { AuctionState } from '.';
import { abbreviateNumber } from '../../utils/helpers';
import { ListingIconField } from './ListingIconField';
import { pluralise } from './utils/pluralise';
import { useCMSData } from '../../state/content-manager';
import { GetInTouch } from '../../components/GetInTouch';

export type ReserveStatus = 'default' | 'success' | 'error';

export type AuctionData = {
  auctionRef: string; // auction id reference for fetching more auction data (bids, offers, history etc)
  type: 'bid' | 'offer';
  count: number;
  views: number;
  watchers: number;
};

const DefaultIconStyles = css`
  fill: ${({ theme }) => theme.colors.neutrals.solidGrays[1]};
  width: 14px;

  ${breakpoint('sm')`
    width: 16px;
  `};
`;

const DefaultHeartIcon = styled(HeartIcon)`
  ${DefaultIconStyles};
`;

const DefaultEyeIcon = styled(EyeIcon)`
  ${DefaultIconStyles};
`;

const StyledListingContextSectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
  row-gap: ${({ theme }) => theme.spacings[1].rem};
`;

const ReferenceNumber = styled(UIText)`
  grid-column: 1;

  color: ${({ theme }) => theme.colors.neutrals.transparentGrays[2]};
`;

const ProductTitle = styled.span`
  grid-column: 1;
  cursor: pointer;

  ${BaseBodyTextStyles};
  text-decoration: none;
  margin: 0 15px 0 0;
`;

const ReserveContainer = styled(ListingIconField)`
  grid-column: 1;

  ${breakpoint('sm')`
    grid-row: 2;
    grid-column: 2;
    align-items:flex-start;
    justify-content: flex-end;
  `};
`;

const ReserveText = styled(BodyText).attrs({ size: 'sm' })`
  color: ${({ theme }) => theme.colors.neutrals.transparentGrays[2]};
  ${breakpoint('sm')`
    text-align: right;
  `}
`;

const AuctionDataContainer = styled.span`
  grid-column: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacings[2].rem};

  > * + * {
    margin-left: ${({ theme }) => theme.spacings[2].rem};
  }

  ${breakpoint('sm')`
    margin-top: 0;
  `};
`;

type QuantityProps = {
  reserveShowing: boolean;
};

const Quantity = styled(BodyText)<QuantityProps>`
  grid-row: 1;
  grid-column: 2;
  text-align: left;
  color: ${({ theme }) => theme.colors.neutrals.transparentGrays[2]};

  ${({ reserveShowing }) =>
    reserveShowing &&
    breakpoint('sm')`
    grid-row:1;
  `}
`;

const ReserveQuantityDataContainer = styled.div`
  grid-row: 3;
  grid-column: 1;
  ${breakpoint('lg')`
     grid-row: 2;
     grid-column: 2;
  `};
`;

const ReserveSuccessIcon = styled(CheckCircleIcon)`
  ${DefaultIconStyles};
  fill: ${({ theme }) => theme.colors.system.systemGreen};
`;

const ReserveFailureIcon = styled(TimesCircleIcon)`
  fill: ${({ theme }) => theme.colors.system.systemRed};
  width: 12px;

  ${breakpoint('sm')`
    width: 14px;
  `};
`;

const StyledMoreOptionsDropDown = styled(DropDown)`
  display: grid;
  grid-row: 1;
  grid-column: 2;
  justify-content: end;
`;

const reserveIcon = {
  success: ReserveSuccessIcon,
  error: ReserveFailureIcon,
  default: undefined,
} as const;

export type ListingAuctionContextSectionProps = {
  title: string;
  referenceNumber: string;
  reserve?: number;
  reserveStatus: ReserveStatus;
  reserveText?: string;
  quantity?: number;
  auctionData?: AuctionData;
  auctionUrl?: string;
  auctionState?: AuctionState;
};

type ListingAuctionContextSectionEventProps = {
  onAuctionDataClick: (auctionRef: string) => void;
};

export const ListingAuctionContextSection: React.FC<
  ListingAuctionContextSectionProps & ListingAuctionContextSectionEventProps
> = ({
  auctionData,
  referenceNumber,
  reserveText,
  reserveStatus,
  title,
  quantity,
  auctionUrl,
  auctionState,
  onAuctionDataClick,
}) => {
  const { itemCardDropdownOptions } = useCMSData();

  const ReserveIcon = reserveIcon[reserveStatus];

  const { setModalProps } = useModal();

  const callModal = (selectedLink: string) => {
    if (!!auctionData) {
      switch (selectedLink) {
        case 'promoteItem':
          setModalProps({
            isOpen: true,
            onClose: () => {},
            children: (
              <PromoteItem itemName={title} lotSku={auctionData.auctionRef} />
            ),
          });
          break;
        case 'contactUs':
          setModalProps({
            isOpen: true,
            onClose: () => {},
            children: <GetInTouch lotSku={auctionData.auctionRef} />,
          });
          break;
        default:
          break;
      }
    }
  };

  return (
    <StyledListingContextSectionGrid>
      <ReferenceNumber size="xs">
        Reference No: {referenceNumber}
      </ReferenceNumber>

      <ProductTitle
        size={{ xs: 'md', sm: 'lg' }}
        onClick={() => {
          TagManager.dataLayer({
            dataLayer: {
              event: `${auctionState}Listings-clickAssetImage`,
            },
          });
          if (auctionUrl) window.open(auctionUrl, '_blank');
        }}
      >
        <strong>{title}</strong>
      </ProductTitle>

      <StyledMoreOptionsDropDown
        options={
          auctionState === 'sold' || auctionState === 'unsold'
            ? itemCardDropdownOptions.slice(1).map((data: any) => ({
                label: data.label,
                value: data.value,
              }))
            : itemCardDropdownOptions.map((data: any) => ({
                label: data.label,
                value: data.value,
              }))
        }
        align="right"
        onChange={(e) => {
          callModal(e);
        }}
        placeholder=""
        selectedValue=""
      />

      {auctionData && (
        <AuctionDataContainer>
          {auctionData.count > 0 && (
            <UIText
              size={{ xs: 'xs', sm: 'sm' }}
              onClick={() => onAuctionDataClick(auctionData.auctionRef)}
            >
              {auctionData.count}{' '}
              {pluralise(auctionData.type, auctionData.count)}
            </UIText>
          )}
          {
            <ListingIconField title="Auction Views" icon={DefaultEyeIcon}>
              {abbreviateNumber(auctionData.views)}
            </ListingIconField>
          }
          {
            <ListingIconField title="Auction Watchers" icon={DefaultHeartIcon}>
              {abbreviateNumber(auctionData.watchers)}
            </ListingIconField>
          }
        </AuctionDataContainer>
      )}
      <ReserveQuantityDataContainer>
        {reserveText && (
          <ReserveContainer icon={ReserveIcon} contentComponent={ReserveText}>
            Reserve : <strong>{reserveText}</strong>
          </ReserveContainer>
        )}

        {quantity && (
          <Quantity
            reserveShowing={!!reserveText}
            size={{ xs: 'xs', sm: 'sm' }}
          >
            Qty: <strong>{quantity}</strong>
          </Quantity>
        )}
      </ReserveQuantityDataContainer>
    </StyledListingContextSectionGrid>
  );
};
